import { AxiosResponse, CancelToken } from 'axios';
import { awBridgeApi } from './Base';
import {
  AWProposalEntry,
  BulkGetRequest,
  EntriesWithNewLog,
  FilterParams,
  ProposalEntryBulkUpdateResponse,
  SearchRequest,
  SearchResponse,
} from './Models';

export function postProposalEntriesSearch(
  params: SearchRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<SearchResponse<AWProposalEntry>>> {
  const path = '/v1/proposal-entries/search';
  return awBridgeApi.post(path, params, { cancelToken });
}
export function getProposalEntry(
  id: string,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AWProposalEntry>> {
  const path = `/v1/proposal-entries/${id}`;
  return awBridgeApi.get(path, { cancelToken, params: { pkg } });
}

export function postBulkGetProposalEntries(
  params: BulkGetRequest,
  pkg: string
): Promise<AxiosResponse<AWProposalEntry[]>> {
  const path = `/v1/proposal-entries/bulkGet`;
  return awBridgeApi.post(path, params, { params: { pkg } });
}

export function putProposalEntriesBulkUpdate(
  data: EntriesWithNewLog[],
  pkg: string,
  awaitCompletion: boolean = false
): Promise<AxiosResponse<ProposalEntryBulkUpdateResponse>> {
  const path = '/v1/proposal-entries';
  return awBridgeApi.put(path, data, {
    params: {
      pkg,
      awaitCompletion,
    },
  });
}

export interface GetProposalEntriesByPageCursorRequest {
  cancelToken?: CancelToken;
  cursor?: string;
  pkg: string;
}

export interface GetAllProposalEntriesByPageCursorRequest extends GetProposalEntriesByPageCursorRequest {
  curProposalEntries: AWProposalEntry[];
}

type ProposalEntriesByCursorResp = {
  proposalEntries: AWProposalEntry[];
  status: {
    code: string;
    message: string;
    details: string[];
  };
  cursor: string;
};

const getProposalEntriesByPageCursor = ({ pkg, cursor, cancelToken }: GetProposalEntriesByPageCursorRequest) => {
  const path = 'v1/proposal-entries/paginateUserEntries';
  return awBridgeApi.get<ProposalEntriesByCursorResp>(path, {
    params: { pkg, cursor, includePrimaryGroupOnly: true },
    cancelToken,
  });
};

export const getAllProposalEntriesByPageCursor = async ({
  curProposalEntries,
  pkg,
  cursor,
  cancelToken,
}: GetAllProposalEntriesByPageCursorRequest): Promise<AWProposalEntry[]> => {
  const {
    data: { proposalEntries, cursor: nextCursor },
  } = await getProposalEntriesByPageCursor({ pkg, cursor, cancelToken });
  if (proposalEntries) {
    const newProposalEntries = curProposalEntries.concat(proposalEntries);
    if (nextCursor) {
      return getAllProposalEntriesByPageCursor({
        curProposalEntries: newProposalEntries,
        pkg,
        cursor: nextCursor,
        cancelToken,
      });
    }
    return newProposalEntries;
  }
  return curProposalEntries;
};

export interface GetAllProposalEntriesBySearchRequest {
  cancelToken?: CancelToken;
  filterParams: FilterParams[];
  limit?: number;
}

export const getAllProposalEntriesBySearch = async ({
  filterParams,
  cancelToken,
  limit = 1000,
}: GetAllProposalEntriesBySearchRequest): Promise<AWProposalEntry[]> => {
  const allEntries: AWProposalEntry[] = [];
  let cursor: string | undefined;

  do {
    const {
      data: { items, metadata },
    } = await postProposalEntriesSearch({ filterParams, limit, cursor }, cancelToken);
    allEntries.push(...items);
    cursor = metadata?.cursor;
  } while (cursor);

  return allEntries;
};
